import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { replaceCartDrawerInnerFromSections } from '@theme/cart-drawer-render';
import { CartLinesUpdateEvent } from '@shopify/events';

const ADD_SELECTOR = '[data-complete-look-add]';
const COMPLETE_LOOK_SELECTOR = 'cart-cross-sell';
const SUCCESS_STATE_DURATION = 400;
const ERROR_STATE_DURATION = 7000;

const pendingVariants = new Set();
let cartMutationQueue = Promise.resolve();

if (!window.shoonyasCompleteLookAddBound) {
  window.shoonyasCompleteLookAddBound = true;
  document.addEventListener('click', handleCompleteLookClick, { capture: true });
}

/**
 * @param {MouseEvent} event
 */
function handleCompleteLookClick(event) {
  const button = event.target instanceof Element ? event.target.closest(ADD_SELECTOR) : null;
  if (!(button instanceof HTMLButtonElement)) return;

  event.preventDefault();
  event.stopPropagation();
  addCompleteLookVariant(button);
}

/**
 * @param {HTMLButtonElement} button
 */
async function addCompleteLookVariant(button) {
  const variantId = button.dataset.variantId;
  if (!variantId || pendingVariants.has(variantId) || button.disabled) return;
  const addContext = {
    button,
    variantId,
    productId: button.dataset.productId || '',
    productHandle: button.dataset.productHandle || '',
    sectionIds: getCartSectionIds(button),
  };

  pendingVariants.add(variantId);
  setButtonPending(button, true);
  setMessage(button, '');

  const queuedMutation = cartMutationQueue.then(() => performCompleteLookAdd(addContext));
  cartMutationQueue = queuedMutation.catch(() => {});

  try {
    await queuedMutation;
  } catch (error) {
    console.error(error);
    setButtonPending(button, false);
    setButtonLabel(button, button.dataset.defaultLabel || '');
    setMessage(button, error instanceof Error && error.message ? error.message : Theme.translations.cart_update_error, true);
  } finally {
    pendingVariants.delete(variantId);
  }
}

/**
 * @param {{ button: HTMLButtonElement, variantId: string, productId: string, productHandle: string, sectionIds: string[] }} context
 */
async function performCompleteLookAdd(context) {
  const { button, variantId, productId, productHandle, sectionIds } = context;
  const addData = await addVariant(variantId, sectionIds);
  const cart = await refreshCart();
  const itemCount = cart?.item_count ?? getItemCountFromSections(addData.sections) ?? 0;

  setButtonPending(button, false);
  setButtonSuccess(button);

  const cartItemsComponent = replaceCartDrawerInnerFromSections(addData.sections, getDrawerSectionId(button));

  dispatchCartLinesUpdate(cartItemsComponent, cart, {
    itemCount,
    productId,
    productHandle,
    variantId,
    sections: addData.sections,
  });

  dispatchCartAdd(cartItemsComponent, { ...addData, item_count: itemCount }, {
    itemCount,
    productId,
    productHandle,
    variantId,
    sections: addData.sections,
  });
}

/**
 * @param {string} variantId
 * @param {string[]} sectionIds
 * @returns {Promise<Record<string, any>>}
 */
async function addVariant(variantId, sectionIds) {
  const payload = {
    items: [{ id: Number(variantId), quantity: 1 }],
    sections: sectionIds.join(','),
    sections_url: window.location.pathname,
  };

  const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify(payload) }));
  const data = await response.json();

  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || Theme.translations.cart_update_error);
  }

  return data;
}

/**
 * @returns {Promise<Record<string, any> | null>}
 */
async function refreshCart() {
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) return null;
  return response.json();
}

/**
 * @param {HTMLButtonElement} button
 * @returns {string[]}
 */
function getCartSectionIds(button) {
  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      sectionIds.add(component.dataset.sectionId);
    }
  });

  const completeLook = button.closest(COMPLETE_LOOK_SELECTOR);
  if (completeLook instanceof HTMLElement && completeLook.dataset.sectionId) {
    sectionIds.add(completeLook.dataset.sectionId);
  }

  return Array.from(sectionIds);
}

/**
 * @param {HTMLButtonElement} button
 * @returns {string | undefined}
 */
function getDrawerSectionId(button) {
  const cartItemsComponent = button.closest('cart-items-component') ?? document.querySelector('cart-items-component[data-drawer]');
  return cartItemsComponent instanceof HTMLElement ? cartItemsComponent.dataset.sectionId : undefined;
}

/**
 * @param {HTMLElement | null} eventTarget
 * @param {Record<string, any>} resource
 * @param {{ itemCount: number, productId: string, productHandle: string, variantId: string, sections?: Record<string, string> }} data
 */
function dispatchCartAdd(eventTarget, resource, data) {
  const target = eventTarget ?? document;
  target.dispatchEvent(
    new CartAddEvent(resource, 'complete-look', {
      source: 'complete-look',
      itemCount: data.itemCount,
      productId: data.productId,
      productHandle: data.productHandle,
      variantId: data.variantId,
      sections: data.sections,
    })
  );
}

/**
 * @param {HTMLElement | null} eventTarget
 * @param {Record<string, any> | null} cart
 * @param {{ itemCount: number, productId: string, productHandle: string, variantId: string, sections?: Record<string, string> }} data
 */
function dispatchCartLinesUpdate(eventTarget, cart, data) {
  const deferredEventPromise = CartLinesUpdateEvent.createPromise();
  const target = eventTarget ?? document;

  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: data.variantId, quantity: 1 }],
      promise: deferredEventPromise.promise,
      detail: {
        source: 'complete-look',
        itemCount: data.itemCount,
        productId: data.productId,
        productHandle: data.productHandle,
        sections: data.sections,
        didError: false,
      },
    })
  );

  deferredEventPromise.resolve({
    cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
    detail: {
      source: 'complete-look',
      itemCount: data.itemCount,
      productId: data.productId,
      productHandle: data.productHandle,
      sections: data.sections,
      didError: false,
    },
  });
}

/**
 * @param {Record<string, string> | undefined} sections
 * @returns {number | null}
 */
function getItemCountFromSections(sections) {
  if (!sections) return null;

  for (const sectionHtml of Object.values(sections)) {
    const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const countText = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    if (!countText) continue;

    const itemCount = parseInt(countText, 10);
    if (!Number.isNaN(itemCount)) return itemCount;
  }

  return null;
}

/**
 * @param {HTMLButtonElement} button
 * @param {boolean} pending
 */
function setButtonPending(button, pending) {
  button.disabled = pending;
  button.setAttribute('aria-busy', pending ? 'true' : 'false');
  button.classList.toggle('is-loading', pending);
  if (pending) {
    setButtonLabel(button, button.dataset.defaultLabel || '');
  }
}

/**
 * @param {HTMLButtonElement} button
 */
function setButtonSuccess(button) {
  button.disabled = true;
  button.setAttribute('aria-busy', 'false');
  setButtonLabel(button, `✓ ${button.dataset.successLabel || Theme.translations.added || ''}`);
}

/**
 * @param {HTMLButtonElement} button
 * @param {string} text
 */
function setButtonLabel(button, text) {
  const label = button.querySelector('.cart-cross-sell-card__button-label');
  if (label) label.textContent = text;
}

/**
 * @param {HTMLButtonElement} button
 * @param {string} text
 * @param {boolean} [isError]
 */
function setMessage(button, text, isError = false) {
  const completeLook = button.closest(COMPLETE_LOOK_SELECTOR);
  const message = completeLook?.querySelector('.cart-cross-sell__message');
  if (!(message instanceof HTMLElement)) return;

  message.textContent = text;
  message.classList.toggle('hidden', text === '');
  message.classList.toggle('cart-cross-sell__message--error', isError);

  if (text !== '') {
    window.setTimeout(() => {
      if (!message.isConnected) return;
      message.textContent = '';
      message.classList.add('hidden');
      message.classList.remove('cart-cross-sell__message--error');
    }, isError ? ERROR_STATE_DURATION : SUCCESS_STATE_DURATION);
  }
}
